'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { createClient } from '../lib/supabase/client';
import { classifyRefreshError } from '../lib/auth/session-controller';
import {
  EXPIRED_REDIRECT_GUARD_MS,
  EXPIRED_REDIRECT_KEY,
  IDLE_TIMEOUT_MS,
  isSignedOutPage,
  loginUrl,
} from '../lib/auth/session-policy';
import { clearStoredTokens } from '../lib/auth/token-cookies';
import {
  notifySignedOut,
  signOutThisDevice,
  startSessionRuntime,
  stopSessionRuntime,
} from '../lib/auth/runtime';
import { nextParamFor } from '../lib/auth/safe-next';
import { readProfile } from '../lib/auth/profile-read';

const AuthContext = createContext<any>({});

const supabase = createClient();

/**
 * AUTH-002: this page loaded with a session that is already dead (the library's start-up
 * refresh was refused and it deleted the session). Clear what is left and go to /login
 * with "Your session ended" — once. A second dead load within the guard window means the
 * cookie could not be cleared; redirecting again would only bounce between /login and
 * the middleware, so stay put instead.
 */
function leaveDeadSession() {
  clearStoredTokens();
  if (typeof window === 'undefined' || isSignedOutPage(window.location.pathname)) return;
  try {
    const last = Number(sessionStorage.getItem(EXPIRED_REDIRECT_KEY)) || 0;
    if (Date.now() - last < EXPIRED_REDIRECT_GUARD_MS) return;
    sessionStorage.setItem(EXPIRED_REDIRECT_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable: redirect anyway (the cookie delete itself is reliable)
  }
  window.location.replace(loginUrl('expired', `${window.location.pathname}${window.location.search}`));
}

/**
 * Session inactivity timeout: 15 minutes without input in ANY tab (AUTH-002 made it
 * cross-tab; D6 set 15 min, superseding SEC-003's 10). Compliance setting — change only by decision. The policy lives in
 * src/lib/auth/session-policy.ts; the timer, the shared activity and the sign-out live in
 * src/lib/auth/runtime.ts + session-controller.ts.
 */
export const INACTIVITY_TIMEOUT_MS = IDLE_TIMEOUT_MS;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);

  // Role booleans
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isComplianceOfficer, setIsComplianceOfficer] = useState(false);
  const [isInventoryStaff, setIsInventoryStaff] = useState(false);
  // clinical_staff: same non-PHI rights as inventory_staff, plus full PHI
  // (RBAC-001 v1.4). Created to enable PHI masking for inventory_staff later.
  const [isClinicalStaff, setIsClinicalStaff] = useState(false);

  const [fullName, setFullName] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);
  // User menu: the primary site's name and how many sites the user is assigned to (USR-003).
  // Display only; null / 0 when unknown or when that part of the read failed.
  const [primarySiteName, setPrimarySiteName] = useState<string | null>(null);
  const [assignedSiteCount, setAssignedSiteCount] = useState<number>(0);
  const [rateLimitError, setRateLimitError] = useState(false);

  const fetchingRef = React.useRef(false);
  const lastFetchedUserIdRef = React.useRef<string | null>(null);
  const signingInRef = useRef(false);

  const clearAuthState = () => {
    setSession(null);
    setUser(null);
    setRole(null);
    setIsSystemAdmin(false);
    setIsAdmin(false);
    setIsComplianceOfficer(false);
    setIsInventoryStaff(false);
    setFullName(null);
    setOrganizationId(null);
    setMustChangePassword(false);
    setPrimarySiteName(null);
    setAssignedSiteCount(0);
    lastFetchedUserIdRef.current = null;
  };

  // ─── Role helper ─────────────────────────────────────────────────────────
  const applyRole = (r: string | null) => {
    setRole(r);
    setIsSystemAdmin(r === 'system_admin');
    // isAdmin is true for system_admin AND admin — both have full org-level access
    setIsAdmin(r === 'system_admin' || r === 'admin');
    setIsComplianceOfficer(r === 'compliance_officer');
    setIsInventoryStaff(r === 'inventory_staff');
    setIsClinicalStaff(r === 'clinical_staff');
  };

  const fetchProfile = async (userId: string, force = false) => {
    if (fetchingRef.current) return;
    if (!force && lastFetchedUserIdRef.current === userId) return;
    fetchingRef.current = true;
    try {
      // The same read, widened with the primary site's name and the assigned sites for the user
      // menu (readProfile). If the widened read fails, the plain read runs again without them:
      // the site is display only and must never block sign-in or the page.
      const { data, error, site } = await readProfile(supabase, userId);
      if (!error && data) {
        applyRole(data.role);
        setFullName(data.full_name || null);
        setOrganizationId(data.organization_id || null);
        setMustChangePassword(data.must_change_password === true);
        setPrimarySiteName(site.primarySiteName);
        setAssignedSiteCount(site.assignedSiteCount);
        lastFetchedUserIdRef.current = userId;
      } else {
        applyRole(null);
        setFullName(null);
        setOrganizationId(null);
        setMustChangePassword(false);
        setPrimarySiteName(null);
        setAssignedSiteCount(0);
      }
    } catch {
      applyRole(null);
      setFullName(null);
      setOrganizationId(null);
      setMustChangePassword(false);
      setPrimarySiteName(null);
      setAssignedSiteCount(0);
    } finally {
      fetchingRef.current = false;
    }
  };

  // Exposed so /account/change-password can pull a fresh profile after
  // clearing the must_change_password flag, releasing the gate without
  // a full page reload.
  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id, true);
    }
  };

  const isRateLimitError = (err: any): boolean => {
    const msg: string = err?.message || '';
    return (
      msg.toLowerCase().includes('rate limit') ||
      msg.toLowerCase().includes('too many requests') ||
      err?.status === 429 ||
      err?.code === 'over_request_rate_limit'
    );
  };

  useEffect(() => {
    let subscriptionObj: { unsubscribe: () => void } | null = null;

    const init = async () => {
      // AUTH-002: getSession() refreshes an expired token itself. If the refresh token is
      // dead the library deletes the session (the cookie delete now works, Partitioned
      // included) and we go to /login once — no retry. A transient failure (network,
      // rate-limit back-off) keeps the session and is left to the runtime's next tick.
      try {
        const { data: sessionData, error } = await supabase.auth.getSession();
        if (error && classifyRefreshError(error) === 'dead') {
          clearAuthState();
          setLoading(false);
          leaveDeadSession();
          return;
        }
        if (sessionData?.session === null && !error) {
          // No session on a protected page: middleware let it through on a session
          // cookie that turned out to be dead. One trip to /login.
          leaveDeadSession();
        }
      } catch {
        // storage failure — fall through to the listener, which reports no session
      }

      const {
        data: { subscription }
      } = supabase.auth.onAuthStateChange((event, newSession) => {
        if (event === 'SIGNED_OUT') {
          clearAuthState();
          setLoading(false);
          // The runtime decides: ignored when we are already signing out; otherwise
          // (dead refresh token, another tab signed out) one sign-out to /login.
          notifySignedOut();
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            fetchProfile(newSession.user.id);
            startSessionRuntime({ freshSignIn: signingInRef.current });
            signingInRef.current = false;
          }
        } else {
          applyRole(null);
          setFullName(null);
          setOrganizationId(null);
          setMustChangePassword(false);
          setPrimarySiteName(null);
          setAssignedSiteCount(0);
          lastFetchedUserIdRef.current = null;
        }

        setLoading(false);
      });

      subscriptionObj = subscription;
    };

    init();

    return () => {
      subscriptionObj?.unsubscribe();
      stopSessionRuntime();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setRateLimitError(false);
    signingInRef.current = true;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      signingInRef.current = false;
      if (isRateLimitError(error)) {
        setRateLimitError(true);
      }
      throw error;
    }
    // A password sign-in is activity: start the session runtime as fresh, so a stale
    // last-activity left by an earlier session cannot sign this one straight out.
    startSessionRuntime({ freshSignIn: true });
    // Operational audit (non-PHI). Record the sign-in so it surfaces in the
    // dashboard "User Activity Today" feed. Emitted ONLY here — the explicit
    // credential-submission success point — and never from onAuthStateChange,
    // which also fires on token refresh / tab focus / reload and would
    // double-log. Login events are operational, not PHI: they go in the
    // Supabase audit_log only (never Cloud SQL / phi_audit_log). Best-effort:
    // a failed audit insert must never block sign-in.
    if (data?.user?.id) {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, organization_id')
          .eq('id', data.user.id)
          .single();
        await supabase.from('audit_log').insert({
          user_name: profile?.full_name || data.user.email || 'Staff',
          action: 'USER_LOGIN',
          details: 'Signed in',
          site: null,
          organization_id: profile?.organization_id ?? null,
        });
      } catch {
        // ignore — audit logging is non-blocking
      }
    }
    return data;
  };

  const signOut = async () => {
    // Operational audit (non-PHI). Record the explicit, user-initiated sign-out
    // BEFORE the session is torn down — the RLS with_check requires an
    // authenticated session. Only this single sign-out point is logged; the
    // inactivity-timeout and invalid-refresh-token paths are not user actions
    // and are intentionally excluded. Best-effort — never block sign-out.
    if (user?.id) {
      try {
        await supabase.from('audit_log').insert({
          user_name: fullName || user.email || 'Staff',
          action: 'USER_LOGOUT',
          details: 'Signed out',
          site: null,
          organization_id: organizationId ?? null,
        });
      } catch {
        // ignore — audit logging is non-blocking
      }
    }
    // AUTH-002 (D5): revoke THIS device's session on the server (`scope: 'local'`),
    // clear local tokens through the single clearStoredTokens(), then /login.
    // Other browsers and devices stay signed in.
    await signOutThisDevice();
    clearAuthState();
  };

  const getCurrentUser = async () => {
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    role,
    // Role booleans
    isSystemAdmin,    // system_admin only — cross-org access
    isAdmin,          // system_admin OR admin — full org-level access
    isComplianceOfficer, // compliance_officer — inventory access + license alerts
    isInventoryStaff, // inventory_staff — standard access
    isClinicalStaff,  // clinical_staff — inventory_staff rights on non-PHI, plus full PHI
    fullName,
    organizationId,
    mustChangePassword,
    primarySiteName,   // user menu: primary site name (profiles.default_site_id), or null
    assignedSiteCount, // user menu: distinct assigned sites (profile_site_assignments)
    refreshProfile,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    rateLimitError,
  };

  return (
    <AuthContext.Provider value={value}>
      <ForcedPasswordChangeGate
        loading={loading}
        user={user}
        mustChangePassword={mustChangePassword}
      />
      {children}
    </AuthContext.Provider>
  );
};

// Force-change-password gate.
//
// When must_change_password=TRUE on the user's profile, redirect every
// non-allowed route to /account/change-password. Direct URL navigation
// (typing a path into the address bar) lands here too because Next.js
// remounts client components on navigation and this effect re-runs.
//
// Allowed paths during the forced-change window:
//   - /account/change-password (the page that clears the flag)
//   - /login, /register, /auth/callback (auth flow entry points)
//   - /signout, /api/signout (signing out)
// Middleware already gates unauth users; this gate runs on top of it.
//
// USR-003 D4: the forced change takes priority over `next`, and keeps it for
// afterwards: the page the user was sent to (or asked for) rides along as
// `/account/change-password?next=…`, and that page goes there once the
// password is changed (safeNextPath).
const FORCED_CHANGE_ALLOWED_PATHS = [
  '/account/change-password',
  '/login',
  '/register',
  '/auth/callback',
  '/signout',
  '/api/signout',
];

function ForcedPasswordChangeGate({
  loading,
  user,
  mustChangePassword,
}: {
  loading: boolean;
  user: any;
  mustChangePassword: boolean;
}) {
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (loading || !user || !mustChangePassword) return;
    if (!pathname) return;
    const isAllowed = FORCED_CHANGE_ALLOWED_PATHS.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`)
    );
    if (!isAllowed) {
      const next = nextParamFor(`${pathname}${window.location.search}`);
      router.replace(
        next
          ? `/account/change-password?next=${encodeURIComponent(next)}`
          : '/account/change-password'
      );
    }
  }, [loading, user, mustChangePassword, pathname, router]);

  return null;
}
